import ArtefactDetailContent from './ArtefactDetailContent';
import type { ArtefactMeta } from './ArtefactDetailContent';

export interface ArtefactTab {
  artefactId: string;
  title: string;
  type: string;
  status: string;
}

interface Props {
  openTabs: ArtefactTab[];
  activeTabId: string;
  onSwitchTab: (artefactId: string) => void;
  onCloseTab: (artefactId: string) => void;
  onMetadataLoaded: (artefactId: string, meta: ArtefactMeta) => void;
}

const TYPE_ICONS: Record<string, string> = {
  prd: '📋',
  sad: '🏗️',
  adr: '📐',
  user_stories: '📝',
  analysis_report: '🔍',
  sprint_plan: '🗓️',
  test_plan: '🧪',
  deployment_runbook: '🚀',
  retro_report: '🔄',
  erd: '🗄️',
  handoff_package: '📦',
};

export default function ArtefactTabs({
  openTabs,
  activeTabId,
  onSwitchTab,
  onCloseTab,
  onMetadataLoaded,
}: Props) {
  if (openTabs.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
        <span className="text-4xl mb-3">📄</span>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13 }}>Select an artefact to view</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Tab bar */}
      <div role="tablist" className="flex items-end overflow-x-auto flex-shrink-0 px-2" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        {openTabs.map(tab => {
          const isActive = tab.artefactId === activeTabId;
          return (
            <div
              key={tab.artefactId}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onSwitchTab(tab.artefactId)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSwitchTab(tab.artefactId);
                }
              }}
              className="group flex items-center gap-1.5 cursor-pointer whitespace-nowrap flex-shrink-0 focus:outline-none"
              style={{
                padding: '10px 16px',
                borderBottom: isActive ? '2px solid var(--primary)' : '2px solid transparent',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                fontWeight: 500,
                color: isActive ? 'var(--text)' : 'var(--text-secondary)',
                maxWidth: 200,
                transition: 'color 150ms, border-color 150ms',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--text)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              <span style={{ fontSize: 16 }}>{TYPE_ICONS[tab.type] ?? '📄'}</span>
              <span className="truncate" style={{ maxWidth: 130 }}>{tab.title}</span>
              <button
                onClick={e => {
                  e.stopPropagation();
                  onCloseTab(tab.artefactId);
                }}
                className="ml-0.5 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex-shrink-0 leading-none focus:outline-none rounded"
                style={{ fontSize: 12, color: 'var(--text-secondary)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--error)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
                aria-label={`Close ${tab.title}`}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      {/* Tab contents — all rendered, inactive hidden via display:none to preserve scroll */}
      <div className="flex-1 min-h-0 relative">
        {openTabs.map(tab => (
          <div
            key={tab.artefactId}
            style={{ display: tab.artefactId === activeTabId ? 'block' : 'none' }}
            className="absolute inset-0 overflow-auto"
          >
            <ArtefactDetailContent
              artefactId={tab.artefactId}
              onMetadataLoaded={meta => onMetadataLoaded(tab.artefactId, meta)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
